import React from "react";

export const PAGE_SIZE = 50;

export const fieldClass =
  "w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2.5 text-sm font-semibold text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-cyan-500/50 [color-scheme:dark]";

export const headerClass =
  "px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500";

export const MiniStat = ({
  label,
  value,
  sub,
  icon,
  tone = "slate",
}: {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ReactNode;
  tone?: "slate" | "cyan" | "emerald" | "amber" | "rose";
}) => {
  const valueTone = {
    slate: "text-slate-100",
    cyan: "text-cyan-400",
    emerald: "text-emerald-400",
    amber: "text-amber-400",
    rose: "text-rose-400",
  }[tone];

  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/60 px-4 py-4">
      <span className="flex items-center gap-2 text-xs font-semibold text-slate-400">
        {icon}
        {label}
      </span>
      <p className={`mt-1.5 text-lg font-black ${valueTone}`}>
        {typeof value === "number" ? value.toLocaleString() : value}
      </p>
      {sub && <p className="text-[10px] font-semibold text-slate-500">{sub}</p>}
    </div>
  );
};

export const EmptyState = ({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) => (
  <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
    <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4 text-slate-500">
      {icon}
    </div>
    <p className="text-sm font-black text-slate-200">{title}</p>
    <p className="max-w-sm text-xs font-semibold leading-relaxed text-slate-500">{body}</p>
  </div>
);

export const LoadingRows = ({ rows, cols }: { rows: number; cols: number }) => (
  <>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <tr key={rowIndex} className="border-t border-slate-800/70">
        {Array.from({ length: cols }).map((__, colIndex) => (
          <td key={colIndex} className="px-5 py-4">
            <div className="h-4 w-full animate-pulse rounded bg-slate-800/80" />
          </td>
        ))}
      </tr>
    ))}
  </>
);

export const SectionCard = ({
  title,
  children,
  action,
}: {
  title: React.ReactNode;
  children: React.ReactNode;
  action?: React.ReactNode;
}) => (
  <div className="overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/40">
    <div className="flex flex-wrap items-center justify-between gap-3 p-6">
      <div>{title}</div>
      {action && <div className="flex flex-wrap items-center gap-2">{action}</div>}
    </div>
    {children}
  </div>
);

export const statusBadge = (status: string | null | undefined, options?: { upper?: boolean }) => {
  const raw = status || "UNKNOWN";
  const label = options?.upper ? raw : raw.toLowerCase().replace(/_/g, " ");
  const upper = raw.toUpperCase();

  let classes =
    "rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wider border ";

  if (["ACTIVE", "ACCEPTED", "SUCCESS", "PAID", "RESOLVED"].includes(upper)) {
    classes += "border-emerald-500/30 bg-emerald-500/10 text-emerald-400";
  } else if (["PENDING", "UNDER_REVIEW", "EARLY_ACCESS_PENDING", "OPEN", "IN_PROGRESS", "REOPEN"].includes(upper)) {
    classes += "border-amber-500/30 bg-amber-500/10 text-amber-400";
  } else if (["REJECTED", "FAILED", "FAIL", "EXPIRED", "CLOSED"].includes(upper)) {
    classes += "border-rose-500/30 bg-rose-500/10 text-rose-400";
  } else {
    classes += "border-slate-700 bg-slate-950/60 text-slate-400";
  }

  return <span className={classes}>{label}</span>;
};

export const planBadge = (planName: string | null | undefined, active: boolean, expired: boolean) => {
  const label = planName && planName.trim() ? planName : "No plan";
  const classes = active
    ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
    : expired
    ? "border-rose-500/30 bg-rose-500/10 text-rose-400"
    : "border-slate-700 bg-slate-950/60 text-slate-400";

  return (
    <span className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wider border ${classes}`}>
      {label}
    </span>
  );
};
